import ArrowDown16 from '@carbon/icons-react/es/arrow--down/16';
import { Tooltip } from 'antd';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { ReactComponent as AddRowBelowIcon } from 'assets/svgs/add-row-below.svg';
import { Button } from 'components/atoms/button';
import { IconShell } from 'components/atoms/icon-shell';
import { StatusIndicator } from 'components/molecules/status-indicator';
import { useProjectTemplateTask } from 'data/templates/projects';
import { useTranslation } from 'hooks/use-translation';
import { TaskMoreActions, useTemplateTaskCommonActions } from '../../commons';
import { MoreActions, NewRow, TextWrapper, Title } from './styles';

export const RenderTaskName: FC<{
  id: number;
  index: number;
  level: number;
  parentTotal: number;
  autoFocus: boolean;
  performAutoFocus(): void;
  onClick(): void;
}> = ({
  id,
  index,
  level,
  parentTotal,
  autoFocus,
  onClick,
  performAutoFocus,
}) => {
  const [tempTaskName, setTempTaskName] = useState('');
  const task = useProjectTemplateTask(id);
  const {
    addSubTask,
    onDeleteTask,
    onUpdateName,
    onUpdateNameAndAddRow,
    onAddTaskBelow,
  } = useTemplateTaskCommonActions(task);
  const addTaskPlaceholder = useTranslation('templates.list.add_task');
  const addSubtaskTooltipText = useTranslation(
    'templates.list.tooltip.add_subtask',
  );
  const isLast = useMemo(() => index === parentTotal - 1, [index, parentTotal]);
  const isLastButOneLevel1Row = useMemo(
    () => level === 1 && index === parentTotal - 2,
    [index, level, parentTotal],
  );

  /**
   * This method is a complicated UX where:
   * 1. If a task name is added initially - it is the last row item per phase. For which, focus out
   *    should not remove the item
   * 2. If a task was manually added by user in between, it should be removed from the table on blur if
   *    the task name is empty and the task is new
   * 3. If a task was added by hitting enter, then if the item was added from last row, then add additional
   *    row below it by invoking the right saga action
   * 4. If a task was added in between, then that shouldn't add an additional row.
   */
  const handleRowChanges = useCallback(() => {
    if (tempTaskName) {
      if (isLast) {
        onUpdateNameAndAddRow(tempTaskName);
      } else {
        onUpdateName(tempTaskName);
      }
      performAutoFocus();
    } else if (task.isNew) {
      if ((level === 1 && !isLast) || level > 1) {
        onDeleteTask();
      }
    }

    return Promise.resolve();
  }, [
    isLast,
    level,
    onDeleteTask,
    onUpdateName,
    onUpdateNameAndAddRow,
    performAutoFocus,
    task.isNew,
    tempTaskName,
  ]);

  const handleAddSubTask = useCallback(() => {
    performAutoFocus();
    addSubTask();
  }, [addSubTask, performAutoFocus]);
  const handleAddTaskBelow = useCallback(() => {
    performAutoFocus();
    onAddTaskBelow();
  }, [onAddTaskBelow, performAutoFocus]);

  return task.isNew ? (
    <NewRow
      autoFocus={autoFocus}
      size="small"
      placeholder={addTaskPlaceholder}
      onAdd={handleRowChanges}
      onBlur={handleRowChanges}
      onUpdate={setTempTaskName}
    />
  ) : (
    <>
      <TextWrapper onClick={onClick}>
        <IconShell
          icon={
            <StatusIndicator
              progress={0}
              status={{
                label: 'To do',
                group: 1,
                color: 'blue',
              }}
              type={task.type}
            />
          }
          size="xsmall"
        />
        <Title>{task?.taskName}</Title>
      </TextWrapper>

      <MoreActions className="more__actions">
        {!isLastButOneLevel1Row && (
          <Tooltip title="Add task below">
            <Button
              icon={<ArrowDown16 />}
              size="small"
              type="text"
              onClick={handleAddTaskBelow}
            />
          </Tooltip>
        )}
        {level < 3 && (
          <Tooltip title={addSubtaskTooltipText}>
            <Button
              icon={<AddRowBelowIcon />}
              size="small"
              type="text"
              onClick={handleAddSubTask}
            />
          </Tooltip>
        )}
        <TaskMoreActions task={task} />
      </MoreActions>
    </>
  );
};
